import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Landmark } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", bank_name: "", currency: "TRY", iban: "", opening_balance: 0 };

export default function BankAccounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/bank-accounts"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form, opening_balance: parseFloat(form.opening_balance) || 0 };
    if (!payload.name) { toast.error("Hesap adı zorunlu"); return; }
    try {
      if (editing) await api.put(`/bank-accounts/${editing.id}`, payload);
      else await api.post("/bank-accounts", payload);
      toast.success(editing ? "Hesap güncellendi" : "Hesap eklendi");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hesabı silmek istiyor musunuz?")) return;
    await api.delete(`/bank-accounts/${id}`);
    toast.success("Silindi"); await load();
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ name: b.name, bank_name: b.bank_name || "", currency: b.currency, iban: b.iban || "", opening_balance: b.opening_balance || 0 });
    setOpen(true);
  };

  const totalByCur = items.reduce((acc, b) => {
    acc[b.currency] = (acc[b.currency] || 0) + (b.current_balance || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-6 slide-up" data-testid="banks-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 mb-2">Banka Hesapları</div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Hesap Bakiyeleri</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Banka hesaplarınızı ve nakitlerinizi tek yerden izleyin.</p>
        </div>
        <Button data-testid="new-bank-btn" onClick={openNew} className="bg-indigo-500 hover:bg-indigo-600"><Plus className="w-4 h-4 mr-2"/>Yeni Hesap</Button>
      </div>

      {Object.keys(totalByCur).length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="bank-totals">
          {Object.entries(totalByCur).map(([cur, total]) => (
            <div key={cur} className="card-elev p-5">
              <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Toplam ({cur})</div>
              <div className="text-2xl font-bold mt-1 mono">{formatMoney(total, cur)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 && (
          <div className="col-span-full card-elev p-12 text-center text-[#94A3B8]">
            <Landmark className="w-10 h-10 mx-auto mb-3 text-[#1E293B]" />
            Henüz banka hesabı yok. İlk hesabı ekleyin.
          </div>
        )}
        {items.map((b) => (
          <div key={b.id} className="card-elev p-6 hover:-translate-y-1 transition-all" data-testid={`bank-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
                <Landmark className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="flex gap-1">
                <button data-testid={`edit-bank-${b.id}`} onClick={() => openEdit(b)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-indigo-400"><Pencil className="w-3.5 h-3.5"/></button>
                <button data-testid={`delete-bank-${b.id}`} onClick={() => remove(b.id)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            </div>
            <div className="mt-4">
              <div className="font-semibold">{b.name}</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">{b.bank_name || "—"}</div>
            </div>
            {b.iban && <div className="text-xs mono text-[#94A3B8] mt-3 truncate">{b.iban}</div>}
            <div className="mt-5 pt-4 border-t border-[#1E293B]">
              <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Güncel Bakiye</div>
              <div className="text-2xl font-bold mono mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {formatMoney(b.current_balance, b.currency)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131D31] border-[#1E293B] text-white">
          <DialogHeader><DialogTitle>{editing ? "Hesabı Düzenle" : "Yeni Banka Hesabı"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#94A3B8]">Hesap Adı</Label>
              <Input data-testid="bank-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" placeholder="Örn: Vadesiz TL" />
            </div>
            <div>
              <Label className="text-xs text-[#94A3B8]">Banka</Label>
              <Input data-testid="bank-institution" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" placeholder="Garanti BBVA" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#94A3B8]">Para Birimi</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="bank-currency" className="bg-[#0F172A] border-[#1E293B] mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="TRY">TRY</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8]">Açılış Bakiyesi</Label>
                <Input type="number" step="0.01" data-testid="bank-opening" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#94A3B8]">IBAN (opsiyonel)</Label>
              <Input data-testid="bank-iban" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" placeholder="TR00 ..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} data-testid="bank-save-btn" className="bg-indigo-500 hover:bg-indigo-600">{editing ? "Güncelle" : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
